import { UsersApi } from "@genesys/cloud-frontend/dist/mjs/apis/UsersApi";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    User,
    UserSearchCriteriaTypeEnum,
    UserSearchCriteriaOperatorEnum,
    UsersSearchResponse,
} from "@genesys/cloud-frontend/dist/mjs/models";
import { RootState } from "app/store";
import { normalize, schema } from "normalizr";
import { UserSearchRequestSortOrderEnum } from "@genesys/cloud-frontend/dist/mjs/models/UserSearchRequest";
import { SearchResult } from "features/qualityForm/formEditor/sidePanel/SearchDropdown";
import { API_CONFIGURATION } from "util/apiUtils";

type UserState = {
    data?: {
        users: Record<string, User>;
    };
};

// Define the schema for the "results" array
const resultSchema = new schema.Entity("listedUsers");

const normalizedUsers = new schema.Entity("users", {
    results: [resultSchema],
});

function searchBody(query: string) {
    return {
        pageSize: 10,
        sortOrder: UserSearchRequestSortOrderEnum.ASC,
        sortBy: "name",
        query:
            query === ""
                ? undefined
                : [
                      {
                          fields: ["name"],
                          operator: UserSearchCriteriaOperatorEnum.AND,
                          value: query.toLowerCase(),
                          type: UserSearchCriteriaTypeEnum.CONTAINS,
                      },
                  ],
    };
}

type UsersReturnBody = {
    searchResponse: UsersSearchResponse | undefined;
    selectedEvaluatorIds: string[];
    selectedEvaluators: User[];
};
export const fetchUsersMultiSelect = createAsyncThunk<
    UsersReturnBody,
    string,
    { state: RootState }
>("users/fetchUsersMultiSelect", async (arg: string, { getState }) => {
    const state = getState();
    const userApi = new UsersApi(API_CONFIGURATION);
    const evaluatorUsers =
        state.scorable.data.scorable?.calibration?.evaluators ?? [];
    const selectedEvaluatorIds = evaluatorUsers.map((user) => user.id ?? "");
    const currentlyLoadedUsers = state.users.data?.users ?? {};
    const selectedEvaluators = selectedEvaluatorIds
        .filter((id) => id in currentlyLoadedUsers)
        .map((id) => currentlyLoadedUsers[id]);
    if (arg === "")
        return {
            searchResponse: undefined,
            selectedEvaluatorIds: selectedEvaluatorIds,
            selectedEvaluators: selectedEvaluators,
        };
    const newLoadedUsers = await userApi
        .postUsersSearch({
            body: searchBody(arg),
        })
        .catch((error) => {
            console.log("Couldn't retrieve users with name", error);
            return undefined;
        });

    return {
        searchResponse: newLoadedUsers,
        selectedEvaluatorIds: selectedEvaluatorIds,
        selectedEvaluators: selectedEvaluators,
    };
});

const memoization = (func: (...args: any[]) => any) => {
    const memory: Record<string, any> = {};
    return (...args: any[]) => {
        const key = JSON.stringify(args);
        if (memory[key]) {
            return memory[key];
        }
        const result = func(...args);
        memory[key] = result;
        return result;
    };
};
const userSearch = memoization(async (arg: string) => {
    const userApi = new UsersApi(API_CONFIGURATION);
    const result = await userApi
        .postUsersSearch({
            body: searchBody(arg),
        })
        .catch((error) => {
            console.log("Couldn't retrieve users with name", error);
            return undefined;
        });
    return result;
});
export const fetchUsers = createAsyncThunk<
    UsersSearchResponse | undefined,
    string,
    { state: RootState }
>("users/fetchUsers", async (arg: string) => {
    return await userSearch(arg);
});

const initialState: UserState = {
    data: {
        users: {},
    },
};

export const usersSlice = createSlice({
    name: "users",
    initialState,
    reducers: {
        clearUsers(state) {
            const data = state.data;
            if (data) {
                data.users = {};
            }
        },
        appendSelectedUser(
            state,
            {
                payload,
            }: PayloadAction<{
                assigneeUser: SearchResult | undefined;
            }>
        ) {
            const data = state.data;
            const users = data?.users;

            if (
                data &&
                payload.assigneeUser &&
                !((payload.assigneeUser.value ?? "") in data.users)
            ) {
                const appendedUser = {
                    id: payload.assigneeUser?.value,
                    name: payload.assigneeUser?.name,
                };
                const normalizedAppendedUser = normalize(
                    { results: [appendedUser] },
                    normalizedUsers
                ).entities.listedUsers;

                data.users = { ...normalizedAppendedUser, ...users };
            }
        },
        removeSelectedUser(
            state,
            {
                payload,
            }: PayloadAction<{
                assigneeUser: SearchResult | undefined;
            }>
        ) {
            const data = state.data;
            const users = { ...data?.users };

            const uuidToRemove = payload.assigneeUser?.value;
            if (data && uuidToRemove && uuidToRemove in users) {
                data.users = Object.entries(users).reduce(
                    (newfilteredUsers, [uuid, user]) => {
                        if (uuid !== uuidToRemove) {
                            newfilteredUsers[uuid] = user;
                        }
                        return newfilteredUsers;
                    },
                    {}
                );
            }
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchUsersMultiSelect.fulfilled, (state, { payload }) => {
                if (!state.data) return;
                const users = payload.searchResponse
                    ? normalize(payload.searchResponse, normalizedUsers)
                          .entities.listedUsers
                    : undefined;
                const preservedUsers = payload.selectedEvaluators
                    ? normalize(
                          { results: payload.selectedEvaluators },
                          normalizedUsers
                      ).entities.listedUsers
                    : undefined;
                const newUsers = users
                    ? Object.fromEntries(
                          Object.entries(users).filter(
                              (user) =>
                                  !payload.selectedEvaluatorIds.includes(
                                      user[0]
                                  )
                          )
                      )
                    : undefined;
                state.data.users = { ...preservedUsers, ...newUsers };
            })
            .addCase(fetchUsersMultiSelect.rejected, (state) => {
                console.log("fetch failed");
                if (state.data) state.data.users = {};
            })
            .addCase(fetchUsers.fulfilled, (state, { payload }) => {
                if (!state.data) return;
                const users = payload
                    ? normalize(payload, normalizedUsers).entities.listedUsers
                    : undefined;
                state.data.users = { ...users };
            })
            .addCase(fetchUsers.rejected, (state) => {
                console.log("fetch failed");
                if (state.data) state.data.users = {};
            });
    },
});
export const usersSliceActions = usersSlice.actions;

export const selectListedUsers = (state: RootState) =>
    state.users.data?.users ?? {};
export const selectUserById = (state: RootState, userId: string) => {
    const user = state.users.data?.users[userId];
    return user ? user.name ?? "" : "";
};

export default usersSlice.reducer;
